import { useEffect, useState, createContext, useContext } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth';
import { useCommon } from '@hooks/useCommon';
import { ADMIN_CLASS } from '@constants/roleconstants';
import './layout.css';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Button, UserProfile } from '@components/shares';
import { MenuProps } from 'antd/es/menu';
import { useWindowSize } from 'usehooks-ts';
import LanguageSelector from '@components/shares/LanguageSelector';
import blankAvatar from '@assets/images/blankavt.jpg';
import SidebarTeacher from '@components/MenuTeacher';
import axios from 'axios';
import { useCalendar } from '@hooks/useCalendar';
import AIConversation from '@components/shares/AIConversation';
import { setShowAIConversation } from '@store/reducers/commonReducer';
import { useSessionWatcher } from '@hooks/useSessionWatcher';

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

export const CalendarContext = createContext({
  selectedDate: new Date(),
  setSelectedDate: (date: Date) => { },
  calendarView: 'week',
  setCalendarView: (view: string) => { },
});
export const useCalendarContextTeacher = () => useContext(CalendarContext);

const SideBar = () => {
  const { width = 0, height = 0 } = useWindowSize();
  const [current, setCurrent] = useState(
    sessionStorage.getItem('selectedKey') || 'dashboard',
  );
  const [collapsed, setCollapsed] = useState(false);

  const onClick: MenuProps['onClick'] = (e) => {
    sessionStorage.setItem('selectedKey', e.key);
    setCurrent(e.key);
  };
  const isSmallScreenSize = width < 1000;
  const handleChangeCollapseMenu = () => {
    setCollapsed(!collapsed);
  };

  return (
    <aside className={`h-screen ${collapsed ? 'w-[80px]' : 'w-[280px]'} transition-all duration-300`}>
      <nav className='min-h-full flex flex-col bg-white border-r shadow-sm relative'>
        <SidebarTeacher isFullSize={collapsed} />
        <button
          className={`bg-primary border-none absolute bottom-5 py-1 pl-2 pr-1 ${collapsed || isSmallScreenSize
            ? 'right-0 rounded-l-sm'
            : 'right-[-28px] rounded-r-sm'
            }`}
          onClick={handleChangeCollapseMenu}
        >
          {collapsed || isSmallScreenSize ? (
            <MenuFoldOutlined className='text-white ' />
          ) : (
            <MenuUnfoldOutlined className='text-white' />
          )}
        </button>
        <div
          className={`flex ${collapsed || isSmallScreenSize ? 'w-[50px]' : 'w-52 ml-5'
            }`}
        ></div>
      </nav>
    </aside>
  );
};

interface LayoutTeacherProps {
  offAiAssistant: boolean | false;
  replaceButton?: boolean | false;
  handleSearchFilterOption?: number;
}

const BreadcrumbNav = () => {
  const location = useLocation();
  const pathSnippets = location.pathname.split('/').filter((i) => i);
  const { t } = useTranslation();

  const extraBreadcrumbItems = pathSnippets
    .map((_, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
      const title =
        pathSnippets[index].charAt(0) + pathSnippets[index].slice(1);
      const translate = t(`breadcrumb.teacher.${title}`);

      // Skip adding breadcrumb for /teacher and /dashboard
      if (
        url === '/teacher' ||
        url === '/dashboard' ||
        url === '/teacher/dashboard'
      ) {
        return null;
      }
      const isId = /^\d+$/.test(title) || /^[0-9a-fA-F-]{16,}$/.test(title);
      if (isId) {
        return null;
      }

      return {
        key: url,
        title: <Link to={url}>{translate}</Link>,
      };
    })
    .filter((item) => item !== null);

  const breadcrumbItems = [
    {
      title: <Link to='/'>Home</Link>,
      key: 'home',
    },
  ].concat(extraBreadcrumbItems);
};

const SideAIConversation = () => {
  const { showAIConversation } = useCommon();
  const { width = 0, height = 0 } = useWindowSize();
  const [current, setCurrent] = useState(
    sessionStorage.getItem('selectedKey') || 'dashboard',
  );
  const [collapsed, setCollapsed] = useState(false);

  const onClick: MenuProps['onClick'] = (e) => {
    sessionStorage.setItem('selectedKey', e.key);
    setCurrent(e.key);
  };
  const isSmallScreenSize = width < 1000;
  const handleChangeCollapseMenu = () => {
    setCollapsed(!collapsed);
  };

  return (
    <aside className={`h-screen ${collapsed ? 'w-[80px]' : 'w-[280px]'} transition-all duration-300`}>
      <nav className='min-h-full flex flex-col bg-white border-r shadow-sm relative'>
        <AIConversation />
        <button
          className={`bg-primary border-none absolute bottom-5 py-1 pl-2 pr-1 ${collapsed || isSmallScreenSize
            ? 'right-0 rounded-l-sm'
            : 'right-[-28px] rounded-r-sm'
            }`}
          onClick={handleChangeCollapseMenu}
        >
          {collapsed || isSmallScreenSize ? (
            <MenuFoldOutlined className='text-white ' />
          ) : (
            <MenuUnfoldOutlined className='text-white' />
          )}
        </button>
        <div
          className={`flex ${collapsed || isSmallScreenSize ? 'w-[50px]' : 'w-52 ml-5'
            }`}
        ></div>
      </nav>
    </aside>
  );
};

export const LayoutTeacher: React.FC<LayoutTeacherProps> = ({
  offAiAssistant,
  replaceButton,
  handleSearchFilterOption,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const isCalendarPage = location.pathname.includes('/calendar');
  const isDashboardPage = location.pathname === '/teacher/dashboard';
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { height = 0 } = useWindowSize();
  const { showAIConversation } = useCommon();
  const [audioUrl, setAudioUrl] = useState('');
  const [openH5pAi, setOpenH5pAi] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: null,
    end: null,
    duration: null,
    color: '#c5e1a5',
    schoolId: null,
    classId: null,
  });
  // Calendar context state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('week');
  const { updateSearchFilter } = useCalendar();

  useEffect(() => {
    if (location.pathname === '/teacher') {
      navigate('/teacher/dashboard');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!auth.access_token || auth.role !== ADMIN_CLASS) {
      navigate('/user/login');
    }
  }, [auth.access_token, auth.role, navigate]);

  useSessionWatcher();

  const answerChat = async (text: string): Promise<string> => {
    try {
      let response = await axios.get(
        `${REACT_APP_API_URL}/api/speech/synthesize`,
        {
          params: { prompt: text },
        },
      );

      if (response.data.synthesized_text) {
        response = await axios.get(`${REACT_APP_API_URL}/api/speech/tts`, {
          params: { text: response.data.synthesized_text },
        });

        if (response.data.audio_url) {
          setAudioUrl(response.data.audio_url);
        }
      }
    } catch (error) {
      console.error('Error fetching TTS:', error);
      throw error;
    }

    return 'Done';
  };

  const onClickBot = () => {
    dispatch(setShowAIConversation(!showAIConversation));
  };
  const handleApplySearchFilter = (value: string) => {
    const option = handleSearchFilterOption || null;
    updateSearchFilter(option, value);
  };

  const isCalendarScreen =
    typeof window !== undefined &&
    window.location.href.includes('/teacher/calendar');
  return (
    <CalendarContext.Provider
      value={{ selectedDate, setSelectedDate, calendarView, setCalendarView }}
    >
      <div className='flex h-screen'>
        <SideBar />
        <div className='w-full flex flex-col overflow-x-auto'>
          <div
            className={`h-[90px] flex flex-row justify-between items-center shadow`}
          >
            {!replaceButton ? (
              <div className='flex items-center px-[30px]'>
                {/* Search components removed for brevity */}
              </div>
            ) : (
              <Button
                className='h-[40px] mr-3 ml-6'
                onClick={() => navigate(`/teacher/dashboard`)}
              >
                Quay lại trang chủ
              </Button>
            )}
            <div className='flex flex-row items-center mr-20 h-[90px]'>
              <div className='mr-12 '>
                <LanguageSelector />
              </div>
              <UserProfile
                imageUrl={auth?.avatar_url ?? blankAvatar}
                avatarSize='md'
                userName={auth?.full_name}
                userRole='Giáo viên'
              />
            </div>
          </div>
          <div
            className={`${isDashboardPage ? 'px-[15px] py-[15px]' : 'px-[30px] py-[30px]'} w-full ${isCalendarPage ? 'no-scroll' : 'overflow-auto'
              }`}
            style={{
              height: `${height - 70}px`,
              backgroundColor: isDashboardPage ? 'oklch(98.5% .005 285)' : undefined
            }}
          >
            <div id='h5p-container'></div>
            <Outlet />
          </div>
        </div>

        {showAIConversation && (
          <div className="fixed right-0 top-0 h-full z-50">
            <SideAIConversation />
          </div>
        )}
      </div>
    </CalendarContext.Provider>
  );
};